import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  differenceInCalendarDays,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  startOfDay,
} from 'date-fns'
import { enGB } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import { ModalTitle } from './modal-title'

// Local replacements for the types that used to come from mui-daterange-picker-plus.
export interface DateRange {
  startDate?: Date
  endDate?: Date
}

export interface DefinedRange {
  label: string
  startDate: Date
  endDate: Date
}

interface DateRangeModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (range: DateRange) => void
  minDate: Date
  maxDate: Date
  initialDateRange: DateRange
  definedRanges: DefinedRange[]
}

const UK_DATE_FORMAT = 'dd/MM/yyyy'

const formatUK = (date: Date) => format(date, UK_DATE_FORMAT)

const numberFormatter = new Intl.NumberFormat('en-GB')
const formatCount = (value: number) => numberFormatter.format(value)

export const DateRangeModal = ({
  open,
  onClose,
  onSubmit,
  minDate,
  maxDate,
  initialDateRange,
  definedRanges,
}: DateRangeModalProps) => {
  const [start, setStart] = useState<Date | null>(initialDateRange.startDate ?? null)
  const [end, setEnd] = useState<Date | null>(initialDateRange.endDate ?? null)

  // Compare by timestamp so a parent that rebuilds Date objects on every render
  // doesn't re-trigger this effect and stamp over what the user just picked.
  const initialStartTime = initialDateRange.startDate?.getTime()
  const initialEndTime = initialDateRange.endDate?.getTime()

  // Re-sync when the modal is (re)opened with a new initial range.
  useEffect(() => {
    if (!open) return
    setStart(initialStartTime != null ? new Date(initialStartTime) : null)
    setEnd(initialEndTime != null ? new Date(initialEndTime) : null)
  }, [open, initialStartTime, initialEndTime])

  const applyPreset = (range: DefinedRange) => {
    setStart(range.startDate)
    setEnd(range.endDate)
  }

  const startError = useMemo(() => {
    if (!start) return 'Choose a start date'
    if (!isValid(start)) return 'Use dd/mm/yyyy'
    if (isBefore(startOfDay(start), startOfDay(minDate)))
      return `Data starts on ${formatUK(minDate)}`
    if (isAfter(startOfDay(start), startOfDay(maxDate)))
      return `Data ends on ${formatUK(maxDate)}`
    if (end && isValid(end) && isAfter(startOfDay(start), startOfDay(end)))
      return 'Start must be on or before the end date'
    return null
  }, [start, end, minDate, maxDate])

  const endError = useMemo(() => {
    if (!end) return 'Choose an end date'
    if (!isValid(end)) return 'Use dd/mm/yyyy'
    if (isAfter(startOfDay(end), startOfDay(maxDate)))
      return `Data ends on ${formatUK(maxDate)}`
    if (isBefore(startOfDay(end), startOfDay(minDate)))
      return `Data starts on ${formatUK(minDate)}`
    return null
  }, [end, minDate, maxDate])

  const invalid = Boolean(startError || endError)

  const summary = useMemo(() => {
    if (!start || !end || !isValid(start) || !isValid(end) || invalid) return null
    const days = differenceInCalendarDays(end, start) + 1
    return `${formatUK(start)} to ${formatUK(end)} (${formatCount(days)} ${
      days === 1 ? 'day' : 'days'
    })`
  }, [start, end, invalid])

  const isPresetActive = (range: DefinedRange) =>
    !!start &&
    !!end &&
    isValid(start) &&
    isValid(end) &&
    isSameDay(range.startDate, start) &&
    isSameDay(range.endDate, end)

  const handleApply = () => {
    if (start && end && !invalid) {
      onSubmit({ startDate: start, endDate: end })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      <ModalTitle onClose={onClose}>Select date range</ModalTitle>
      <DialogContent sx={{ pt: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2.5, sm: 3 }}
          >
            {definedRanges.length > 0 && (
              <Box
                sx={{
                  width: { xs: '100%', sm: 260 },
                  flexShrink: 0,
                  pr: { sm: 3 },
                  pb: { xs: 2.5, sm: 0 },
                  borderRight: { xs: 0, sm: 1 },
                  borderBottom: { xs: 1, sm: 0 },
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Quick ranges
                </Typography>
                <List dense disablePadding>
                  {definedRanges.map((range) => (
                    <ListItemButton
                      key={range.label}
                      selected={isPresetActive(range)}
                      onClick={() => applyPreset(range)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          '& .MuiListItemText-primary': { fontWeight: 600 },
                        },
                      }}
                    >
                      <ListItemText
                        primary={range.label}
                        secondary={`${formatUK(range.startDate)} – ${formatUK(
                          range.endDate,
                        )}`}
                        slotProps={{
                          primary: { variant: 'body2', noWrap: true },
                          secondary: { variant: 'caption', noWrap: true },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}

            <Stack
              spacing={2.5}
              sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 420 }, pt: { sm: 0.5 } }}
            >
              <DatePicker
                label="Start date"
                value={start}
                format={UK_DATE_FORMAT}
                minDate={minDate}
                maxDate={end && isValid(end) ? end : maxDate}
                onChange={(newValue) => setStart(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(startError) && start !== null,
                    helperText: (start !== null && startError) || ' ',
                  },
                }}
              />
              <DatePicker
                label="End date"
                value={end}
                format={UK_DATE_FORMAT}
                minDate={start && isValid(start) ? start : minDate}
                maxDate={maxDate}
                onChange={(newValue) => setEnd(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(endError) && end !== null,
                    helperText: (end !== null && endError) || ' ',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Data available from {formatUK(minDate)} to {formatUK(maxDate)}.
              </Typography>
            </Stack>
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          gap: 1,
          justifyContent: 'space-between',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {summary ?? 'No range selected'}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={invalid}
            onClick={handleApply}
          >
            Apply range
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}